import { useState } from 'react'
import { advanceBuildStatus } from '../buildApi.js'
import { useAccess } from '../../../lib/access.jsx'
import { ACTIONS } from '../../../lib/rbac.js'
import styles from './Studio.module.css'

/**
 * VERSION HISTORY — draft → proof → submitted → production (Master Spec
 * Section 10).
 *
 * EVERY RENDER LANDS AS A DRAFT and stays there. The backend
 * (`renderService.js`) has no code path that advances a status on its own:
 * `setBuildStatus` is only reachable from `POST /api/artwork/build/:id/status`,
 * which is this button. There is no auto-publish anywhere in the chain.
 *
 * Moves are ONE STEP FORWARD along the sequence, because that is what a version
 * history is — jumping a draft straight to production would leave no proof and
 * no submission on the record. A note is carried on every move so the trail
 * says WHY, not just when.
 *
 * `can()` is a DRAWING decision (hard rule #5). The route re-decides nothing
 * per-caller today, so this gate is UX — flagged in CLAUDE.md, not described
 * as security.
 */
export default function StatusAdvance({ record, statuses, onAdvanced }) {
  const { can, why } = useAccess()
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [open, setOpen] = useState(false)

  const order = statuses && statuses.length ? statuses : ['draft', 'proof', 'submitted', 'production']
  const current = record?.status || 'draft'
  const idx = order.indexOf(current)
  const next = idx >= 0 && idx < order.length - 1 ? order[idx + 1] : null

  const allowed = can(ACTIONS.ARTWORK_BUILD_SUBMIT)

  async function advance() {
    if (!next || !record?.buildId) return
    setBusy(true)
    setErr('')
    try {
      const res = await advanceBuildStatus(
        record.buildId,
        { status: next, note: note.trim() },
        { before: { status: current } }
      )
      setNote('')
      setOpen(false)
      onAdvanced?.(res?.record || { ...record, status: next })
    } catch (e) {
      setErr(e?.message || 'Could not advance the status.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={styles.statusBar}>
      <div className={styles.statusTrail}>
        {order.map((st) => {
          const at = order.indexOf(st)
          return (
            <span
              key={st}
              className={`${styles.statusStep} ${at < idx ? styles.statusPast : ''} ${st === current ? styles.statusNow : ''}`}
            >
              {st}
            </span>
          )
        })}
      </div>

      {!next ? (
        <span className={styles.note}>This build is at the end of the version history.</span>
      ) : !allowed ? (
        <span className={styles.note}>{why(ACTIONS.ARTWORK_BUILD_SUBMIT) || 'You cannot advance a build status.'}</span>
      ) : !open ? (
        <button type="button" className="btn" onClick={() => setOpen(true)}>Advance to {next}</button>
      ) : (
        <div className={styles.fallbackRow}>
          <input
            className="input"
            placeholder={`Note for the ${current} → ${next} move`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <button type="button" className="btn btn-primary" onClick={advance} disabled={busy}>
            {busy ? 'Saving…' : `Confirm ${next}`}
          </button>
          <button type="button" className={styles.linkBtn} onClick={() => setOpen(false)}>Cancel</button>
        </div>
      )}
      {err && <p className={styles.warnNote}>{err}</p>}
    </div>
  )
}
